import React from 'react'
import ButtonPrimary from './ButtonPrimary'
import ButtonSecondary from './ButtonSecondary'
import ButtonSecondaryUpgraded from './ButtonSecondaryUpgraded'
import ButtonGradient from './ButtonGradient'
import ButtonGradientWhite from './ButtonGradientWhite'
import ButtonGradientTransparent from './ButtonGradientTransparent'
import ButtonLink from './ButtonLink'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary-gold'
  | 'gradient'
  | 'gradient-white'
  | 'gradient-transparent'
  | 'link'

export interface ButtonProps {
  variant?: ButtonVariant
  size?: 'small' | 'medium' | 'large'
  shape?: 'rectangle' | 'square' | 'circle'
  children?: React.ReactNode
  disabled?: boolean
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  fullWidth?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  letter?: string
  style?: React.CSSProperties
  ariaLabel?: string
  'data-testid'?: string
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  children,
  disabled = false,
  onClick,
  className = '',
  fullWidth = false,
  icon,
  iconPosition = 'left',
  shape = 'rectangle',
  size = 'medium',
  letter,
  style,
  ariaLabel,
  'data-testid': dataTestId
}) => {
  const commonProps = {
    children,
    ariaLabel,
    disabled,
    onClick,
    className,
    fullWidth,
    icon,
    iconPosition,
    shape,
    size,
    letter,
    style,
    'data-testid': dataTestId
  }

  switch (variant) {
    case 'primary':
      return <ButtonPrimary {...commonProps} />

    case 'secondary':
      return <ButtonSecondary {...commonProps} />

    case 'secondary-gold': {
      // ButtonSecondaryUpgraded takes no icon props, so the icon is rendered as part of the children
      const goldChildren = (
        <>
          {icon && iconPosition === 'left' && (
            <span style={{ marginRight: '8px', display: 'inline-flex' }}>{icon}</span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span style={{ marginLeft: '8px', display: 'inline-flex' }}>{icon}</span>
          )}
        </>
      )
      return (
        <ButtonSecondaryUpgraded
          variant="gold"
          onClick={onClick}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          sx={style ? { ...style } : undefined}
          className={className}
          data-testid={dataTestId}
        >
          {goldChildren}
        </ButtonSecondaryUpgraded>
      )
    }

    case 'gradient':
      return <ButtonGradient {...commonProps} />

    case 'gradient-white':
      return <ButtonGradientWhite {...commonProps} />

    case 'gradient-transparent':
      return <ButtonGradientTransparent {...commonProps} />

    case 'link':
      return <ButtonLink {...commonProps} />

    default:
      return <ButtonPrimary {...commonProps} />
  }
}

export default Button
